import { Guest } from './GuestRow';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { CheckCheckIcon, CheckIcon, X } from 'lucide-react';

interface WaitingListModalProps {
  isOpen: boolean;
  onClose: () => void;
  waitingGuests: Guest[];
  onStatusChange: (guestId: string, status: 'approved' | 'rejected') => void;
  onRemove: (guestId: string) => void;
  onMoveFromWaitingList: (guestId: string) => void; // Nueva función
}

export const WaitingListModal = ({ 
  isOpen, 
  onClose, 
  waitingGuests, 
  onStatusChange,
  onRemove,
  onMoveFromWaitingList
}: WaitingListModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            Waiting list ({waitingGuests.length})
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {waitingGuests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No guests in waiting list</p>
          </div>
        ) : (
          <div className="space-y-0">
            <table className="w-full text-sm table-fixed">
              <thead className="text-muted-foreground text-left border-b">
                <tr>
                  <th className="pb-2 w-12"></th>
                  <th className="pb-2 w-48">Guests</th>
                  <th className="pb-2 w-64">Email</th>
                  <th className="pb-2 w-auto">Status</th>
                </tr>
              </thead>
              <tbody>
                {waitingGuests.map((guest) => (
                  <tr key={guest.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-2 w-12">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          {guest.avatar_url ? (
                            <img 
                              src={guest.avatar_url} 
                              alt={guest.name} 
                              className="w-8 h-8 rounded-full" 
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-600">
                              {guest.name?.charAt(0) || guest.email.charAt(0)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 w-48">
                      <div>
                        <div className="font-medium text-gray-900">
                          {guest.name || guest.email.split('@')[0]}
                        </div>
                        {guest.waiting_list_position && (
                          <div className="text-xs text-gray-500">
                            Position #{guest.waiting_list_position}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 w-64">
                      <div className="text-sm text-gray-600">{guest.email}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline-approve"
                          size="sm"
                          onClick={() => onMoveFromWaitingList(guest.id)}
                          className="flex items-center space-x-1 font-medium"
                        >
                          <CheckIcon className="w-4 h-4" />
                          <span>Approve</span>
                        </Button>
                        <Button
                          variant="outline-deny"
                          size="sm"
                          onClick={() => onRemove(guest.id)}
                          className="flex items-center space-x-1 font-medium"
                        >
                          <X className="w-4 h-4" />
                          <span>Deny</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};